import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import useShowRatePlan from '../../../services/rate-plan/hooks/useShowRatePlan';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import FormRateAvailability from './restriction-setup/FormRateAvailability';
import FormRestriction from './restriction-setup/FormRestriction';
import { SelectedCell } from '../components/MonthlySetupTable'; // Pastikan path import benar
import { toast } from 'sonner';

// Props yang diterima oleh komponen
interface RestrictionSetupProps {
    show: boolean;
    onClose: () => void;
    onSuccess: () => void; // Untuk membersihkan state setelah berhasil
    selection: SelectedCell[];
}

// Daftar tipe restriksi yang akan ditangani oleh FormRestriction
export const RestrictionTypesList = [
    "closed_to_arrival",
    "closed_to_departure",
    "max_stay",
    "min_stay_arrival",
    "min_stay_through",
    "stop_sell",
];

const RestrictionSetup: React.FC<RestrictionSetupProps> = ({ show, onClose, onSuccess, selection }) => {
    const [isValidSelection, setIsValidSelection] = useState(false);
    const [rateId, setRateId] = useState<string | null>(null);
    const [formType, setFormType] = useState<string | null>(null);

    // Memoize logika validasi agar tidak berjalan pada setiap render ulang.
    // Ini penting untuk performa.
    const validationResult = useMemo(() => {
        if (!selection || selection.length === 0) {
            return { valid: false, message: 'No cells selected.' };
        }

        const firstCell = selection[0];
        const { rateId, restrictionType } = firstCell;

        // Validasi 1: Pastikan semua sel yang dipilih memiliki rateId yang sama.
        const allSameRateId = selection.every(cell => cell.rateId === rateId);
        if (!allSameRateId) {
            return { valid: false, message: 'Cannot edit different rate plans at the same time.' };
        }

        // Validasi 2: Pastikan semua sel yang dipilih memiliki restrictionType yang sama.
        const allSameRestrictionType = selection.every(cell => cell.restrictionType === restrictionType);
        if (!allSameRestrictionType) {
            return { valid: false, message: 'Cannot edit different restriction types (e.g., Rate and CTA) at the same time.' };
        }

        // Jika semua validasi lolos, kembalikan hasil yang valid.
        return { valid: true, rateId, restrictionType, message: 'Selection is valid' };
    }, [selection]);

    // Efek ini berjalan ketika komponen ditampilkan (`show` menjadi true).
    useEffect(() => {
        if (show) {
            const { valid, rateId, restrictionType, message } = validationResult;
            setIsValidSelection(valid);

            if (valid) {
                // Jika valid, set state yang diperlukan untuk mengambil data rate plan dan merender form.
                setRateId(rateId!);
                setFormType(restrictionType!);
            } else {
                // Jika tidak valid, tampilkan pesan error dan tutup dialog.
                toast.error(message, { id: 'selection-validation-error' });
                onClose();
            }
        }
    }, [show, validationResult, onClose]);

    // Mengambil detail rate plan berdasarkan rateId yang valid.
    const { data: rateplan, isError, isSuccess, isFetching } = useShowRatePlan({
        id: rateId ?? undefined, // Hanya fetch jika rateId ada
        include: 'roomType'
    });

    // Handler jika tipe restriksi diubah di dalam form (seharusnya dinonaktifkan di mode bulk).
    const handleFormTypeChange = (newType: string) => {
        if (newType !== formType) {
            toast.warning("Changing restriction type is not recommended in bulk edit mode as it will reset the form.");
            setFormType(newType);
        }
    };

    // Fungsi untuk merender form yang benar berdasarkan tipe restriksi.
    const renderForm = () => {
        // Jangan render apapun jika seleksi tidak valid atau data belum siap.
        if (!isValidSelection || !rateId || !formType) {
            return null;
        }
        
        // Jika tipe adalah 'rate', render FormRateAvailability.
        if (formType === 'rate') {
            return (
                <FormRateAvailability
                    selection={selection}
                    rateplan={rateplan}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    isFetching={isFetching}
                    isError={isError}
                    isSuccess={isSuccess}
                    onTypeChange={handleFormTypeChange}
                />
            );
        }

        // Jika tipe ada dalam daftar RestrictionTypesList, render FormRestriction.
        if (RestrictionTypesList.includes(formType)) {
            return (
                <FormRestriction
                    selection={selection}
                    rateplan={rateplan}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    isFetching={isFetching}
                    isError={isError}
                    isSuccess={isSuccess}
                    onTypeChange={handleFormTypeChange}
                />
            );
        }

        // Return null jika tipe tidak dikenali.
        return null;
    };

    return (
        // Modal hanya akan ditampilkan jika 'show' adalah true DAN seleksi valid.
        <Modal show={show && isValidSelection} onHide={onClose} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title>Bulk Setup</Modal.Title>
            </Modal.Header>
            {renderForm()}
        </Modal>
    );
};

export default RestrictionSetup;